import { isAppRole, type AppRole } from '@/lib/security/rbac';

export interface AuthProfileRouting {
  role?: string | null;
  primaryRole?: string | null;
  roles?: readonly string[] | null;
}

const ADMIN_HOME_ROLES = new Set(['super_admin', 'admin', 'manager']);
const STAFF_HOME_ROLES = new Set(['dialgia_admin', 'dialgia_pm', 'dialgia_dev']);
const CLIENT_HOME_ROLES = new Set(['client', 'client_admin', 'client_user', 'user']);
const AUTH_ENTRY_PATHS = new Set(['/auth', '/auth/callback', '/client-login']);

export const getAuthenticatedRoles = (profile?: AuthProfileRouting | null): AppRole[] => (
  [...new Set(
    [
      ...(profile?.roles ?? []),
      profile?.primaryRole,
      profile?.role,
    ].filter((role): role is string => Boolean(role)),
  )].filter(isAppRole)
);

export const getDefaultAuthenticatedRoute = (
  profile?: AuthProfileRouting | null,
): string => {
  const roles = getAuthenticatedRoles(profile);

  if (roles.some((role) => ADMIN_HOME_ROLES.has(role))) return '/admin';
  if (roles.some((role) => STAFF_HOME_ROLES.has(role))) return '/portal';
  if (roles.some((role) => CLIENT_HOME_ROLES.has(role))) return '/client-hub';

  return '/profile';
};

export const getSafeAuthRedirect = (
  requestedPath: string | null | undefined,
  fallbackPath: string,
): string => {
  if (!requestedPath || !requestedPath.startsWith('/') || requestedPath.startsWith('//')) {
    return fallbackPath;
  }

  if (requestedPath.includes('\\')) return fallbackPath;

  try {
    const parsed = new URL(requestedPath, 'https://dialgia.invalid');
    if (parsed.origin !== 'https://dialgia.invalid') return fallbackPath;
    if (AUTH_ENTRY_PATHS.has(parsed.pathname)) return fallbackPath;

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallbackPath;
  }
};
